import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useT } from "../i18n/LanguageContext.jsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OverviewPage() {
  const [health, setHealth] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(null);
  const { t } = useT();

  useEffect(() => {
    (async () => {
      try {
        const [h, c] = await Promise.all([
          axios.get(`${API}/health`),
          axios.get(`${API}/config`),
        ]);
        setHealth(h.data);
        setConfig(c.data);
      } catch (e) {
        setError(String(e.message || e));
      }
    })();
  }, []);

  const cards = [
    {
      label: t("ov.card.variables"),
      value: health?.variables ?? "—",
      sub: t("ov.card.variables.sub"),
      testid: "card-variables",
    },
    {
      label: t("ov.card.classes"),
      value: config?.lda?.classes?.length ?? "—",
      sub: t("ov.card.classes.sub"),
      testid: "card-classes",
    },
    {
      label: t("ov.card.rules"),
      value: health?.rules ?? "—",
      sub: t("ov.card.rules.sub"),
      testid: "card-rules",
    },
    {
      label: t("ov.card.version"),
      value: health?.config_version ?? "—",
      sub: config?.source ?? "",
      testid: "card-version",
    },
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-12" data-testid="overview-page">
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 mb-12">
        <div>
          <div className="flex items-center gap-8">
            <img
              src="https://customer-assets.emergentagent.com/job_sepsis-index-api/artifacts/2wfrbxim_SIF_logo2.png"
              alt="Sepsi Index Framework"
              className="w-56 h-56 object-contain flex-shrink-0"
              data-testid="overview-logo"
            />
            <div>
              <div className="data-label mb-4">{t("ov.kicker")}</div>
              <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
                {t("ov.titleA")}
                <br />
                <span className="text-slate-400">{t("ov.titleB")}</span>
              </h1>
            </div>
          </div>
          <p className="text-base text-slate-600 max-w-xl leading-relaxed mt-6">
            {t("ov.description")}
          </p>
          <div className="flex items-center gap-3 mt-8">
            <Link
              to="/simulator"
              data-testid="btn-open-simulator"
              className="px-5 py-2.5 bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 border border-slate-900"
            >
              {t("ov.openSimulator")}
            </Link>
            <Link
              to="/config"
              data-testid="btn-open-config"
              className="px-5 py-2.5 bg-white text-slate-900 text-sm font-medium hover:bg-slate-50 border border-slate-300"
            >
              {t("ov.editConfig")}
            </Link>
          </div>
          {error && (
            <div className="mt-6 p-3 border border-red-300 bg-red-50 text-red-800 text-sm" data-testid="overview-error">
              {error}
            </div>
          )}

          <div
            className="mt-12 pt-8 border-t border-slate-200 flex items-center gap-6"
            data-testid="sponsor-overview"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_sepsis-index-api/artifacts/x8tz82dp_StarHealth_central.jpeg"
              alt="StarHealth"
              className="h-28 w-auto object-contain"
            />
            <div>
              <div className="data-label mb-1">{t("ov.sponsor.label")}</div>
              <div className="text-lg font-semibold text-slate-900 leading-tight">StarHealth</div>
              <div className="text-sm text-slate-500 font-mono tracking-wide">
                {t("ov.sponsor.tagline")}
              </div>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 p-6 clinical-grid">
          <div className="data-label mb-4">{t("ov.activeConfig")}</div>
          <div className="grid grid-cols-2 gap-px bg-slate-200">
            {cards.map((c) => (
              <div key={c.label} className="bg-white p-5" data-testid={c.testid}>
                <div className="data-label">{c.label}</div>
                <div className="font-mono text-3xl tabular-nums text-slate-900 mt-2">
                  {c.value}
                </div>
                <div className="text-xs text-slate-500 mt-1">{c.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="border-t border-slate-200 pt-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="data-label mb-2">{t("ov.step")} 01</div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {t("ov.step1.title")}
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">{t("ov.step1.body")}</p>
          </div>
          <div>
            <div className="data-label mb-2">{t("ov.step")} 02</div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {t("ov.step2.title")}
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">{t("ov.step2.body")}</p>
          </div>
          <div>
            <div className="data-label mb-2">{t("ov.step")} 03</div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {t("ov.step3.title")}
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed mb-3">
              {t("ov.step3.bodyIntro")}
            </p>
            <ul className="text-sm text-slate-600 leading-relaxed space-y-1.5 list-disc pl-5">
              <li>{t("ov.step3.bullet1")}</li>
              <li>{t("ov.step3.bullet2")}</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
